import api from "./apiClient";

// Bulk Reporting Tool (ASB-3991): the central API's /bulk-reports routes
// (arcspan_sandbox backend/api/users/v2/bulk_report_api.py, ASB-4120). Every
// call names the ArcTag by its numeric tag_id, never the subdomain; the API
// checks the caller may see it. Errors arrive as apiClient errors carrying
// .status and .data ({ message: "<code>" }); bulkReportErrors.js turns them
// into copy.
//
// Report generation is asynchronous: submitJob queues a job and the job calls
// below read, download, cancel and acknowledge it (the job flow, ASB-4295).

// Orders and line items go by GAM ID; labels are display only (ASB-4727).
// A 503 means the campaign lookups are unavailable. apiClient would retry a 5xx
// twice, 2 s apart, and for a POST re-submit. Off here.
const NO_RETRY = { retryServerErrors: false };

const toTagId = (tagId) => {
	const id = typeof tagId === "number" ? tagId : parseInt(tagId, 10);
	if (!Number.isInteger(id) || id <= 0) throw new Error(`Invalid tag_id: ${tagId}`);
	return id;
};

const query = (params) => {
	const qs = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== "") qs.set(key, String(value));
	});
	return qs.toString();
};

const jobPath = (jobId) => `/bulk-reports/jobs/${encodeURIComponent(jobId)}`;

// apiClient throws an Error carrying .status / .data on every path (since
// ASB-3371 also on the request it retries after refreshing the token, which
// used to throw the raw Response). Kept as a guard: a raw Response from any
// other source is read once and thrown in the usual shape, so the error copy
// (and the codes the page acts on: no_data, terminal, expired) stays the same.
const isResponse = (value) =>
	!!value && !(value instanceof Error) && typeof value.status === "number" && typeof value.json === "function";

export async function normalizeError(error) {
	if (!isResponse(error)) return error;
	const normalized = new Error(`API request failed: ${error.status} ${error.statusText || ""}`.trim());
	normalized.status = error.status;
	normalized.response = error;
	try {
		const data = await error.json();
		normalized.data = data;
		if (data && typeof data.message === "string" && data.message) normalized.message = data.message;
	} catch (e) {
		// Not JSON (a gateway page): status only.
	}
	return normalized;
}

// Every call goes through here so a thrown Response never reaches the page.
const call = async (request) => {
	try {
		return await request();
	} catch (error) {
		throw await normalizeError(error);
	}
};

// The ArcTag's data window and its campaigns, newest first:
// { available_from, available_to, region, orders: [{ value, label, advertiser_label, first_day, last_day }] }.
// 404 no_data when the ArcTag has no data in the window. Show `label`, send `value`.
export async function getOptions(tagId) {
	const response = await call(() => api.get(`/bulk-reports/options?${query({ tag_id: toTagId(tagId) })}`, NO_RETRY));
	return (await response.json()).data;
}

// One campaign's flight and filter values:
// { first_day, last_day, line_items: [{ value, label }], countries, devices, domains, event_types }.
// 404 order_not_found.
export async function getOrderOptions(tagId, orderId) {
	const response = await call(() =>
		api.get(`/bulk-reports/options/${encodeURIComponent(orderId)}?${query({ tag_id: toTagId(tagId) })}`, NO_RETRY),
	);
	return (await response.json()).data;
}

// The API's filter keys are singular. A multi-select sends its values as a
// list (line items as their integer IDs, unfiltered: the API refuses a non-ID
// with 400 rather than widening the report); Event Type sends one string. An
// empty selection is no filter, so the key is left out.
export function toApiFilters({ lineItem, country, device, domain, eventType } = {}) {
	const filters = {};
	const list = (values) => (values || []).filter((v) => typeof v === "string" && v !== "");
	const ids = (values) => (values || []).filter((v) => v !== undefined && v !== null && v !== "");
	if (ids(lineItem).length) filters.line_item = ids(lineItem);
	if (list(country).length) filters.country = list(country);
	if (list(device).length) filters.device = list(device);
	if (list(domain).length) filters.domain = list(domain);
	if (eventType) filters.event_type = eventType;
	return filters;
}

// Queues a report. The body carries exactly the seven keys the API accepts
// (it refuses any other set with invalid_request). Resolves to { job, reused }:
// reused true means an identical report finished in the last 24 hours (200);
// otherwise the job is new or an identical one already in progress (202).
// orderId: the order's `value`. requestedVia: "download" | "email".
export async function submitJob({ tagId, orderId, dateStart, dateEnd, filters, sections, requestedVia }) {
	const body = {
		tag_id: toTagId(tagId),
		order_id: orderId,
		date_start: dateStart,
		date_end: dateEnd,
		filters: filters || {},
		sections,
		requested_via: requestedVia,
	};
	const response = await call(() => api.post("/bulk-reports/jobs", body, NO_RETRY));
	const json = await response.json();
	return { job: json.data, reused: !!json.reused };
}

// Jobs on the ArcTag, newest first, 30 days by default. mine: only the
// caller's ("My Reports"); without it, everyone's on the tag. status: a list
// or a comma string, e.g. ["queued", "running"]. since / until: YYYY-MM-DD.
// Resolves to { jobs, pagination: { page, per_page, total_items, total_pages } }.
// requestOptions go to apiClient: the page's poll passes retryServerErrors:
// false, so a 5xx waits for the next (backed-off) tick instead of retrying.
export async function listJobs(tagId, { mine, status, since, until, page, perPage } = {}, requestOptions = {}) {
	const qs = query({
		tag_id: toTagId(tagId),
		mine: mine ? 1 : undefined,
		status: Array.isArray(status) ? status.join(",") : status,
		since,
		until,
		page,
		per_page: perPage,
	});
	const response = await call(() => api.get(`/bulk-reports/jobs?${qs}`, requestOptions));
	const json = await response.json();
	return { jobs: json.data || [], pagination: json.pagination };
}

export async function getJob(jobId, requestOptions = {}) {
	const response = await call(() => api.get(jobPath(jobId), requestOptions));
	return (await response.json()).data;
}

// A fresh presigned link per click: { url, expires_at } (valid 300 s).
// 404 not_ready, 410 expired.
export async function getJobDownload(jobId) {
	const response = await call(() => api.get(`${jobPath(jobId)}/download`));
	return (await response.json()).data;
}

// 403 not_owner, 409 terminal. Resolves to the job.
export async function cancelJob(jobId) {
	const response = await call(() => api.post(`${jobPath(jobId)}/cancel`, {}, NO_RETRY));
	return (await response.json()).data;
}

// Marks a failed job seen so it does not toast on the next visit.
// 403 not_owner, 409 not_failed. Resolves to the job.
export async function acknowledgeJob(jobId) {
	const response = await call(() => api.post(`${jobPath(jobId)}/acknowledge`, {}, NO_RETRY));
	return (await response.json()).data;
}

// The caller's own failed, unacknowledged jobs from the last 7 days.
export async function getUnacknowledgedFailures() {
	const response = await call(() => api.get("/bulk-reports/jobs/unacknowledged-failures"));
	return (await response.json()).data || [];
}
